import { useEffect } from 'react';
import { useTracking } from 'react-tracking';
import Media from 'react-media';
import loadable from '@loadable/component';
import PlusSolid from '@simplywallst/ui-core/icons/PlusSolid';
import { useTheme } from '@simplywallst/ui-core';
import { Badge } from '@simplywallst/ui-core/next';
import { useUserAgent } from '@simplywallst/hooks/useUserAgent';

import { SubSectionHeader } from '../../Overview/components/SubSectionHeader';
import { Section } from '../../Overview/components/Section';
import { AddButton, NotesContainer, StyledHeaderGrid } from './styled';

import { useCompanyNotes } from '@/components/Note/hooks/useCompanyNotes';
import { useCompanyNoteModal } from '@/components/Note/components/CompanyNoteModalContext';
import { useOverviewTranslation } from '../../OverviewSection/hooks/useOverviewTranslation';

const Note = loadable(() => import('@components/Note/components/Note'));
const EmptyState = loadable(
  () => import('@components/Note/components/EmptyState')
);
const SeeMoreButton = loadable(() => import('./SeeMoreButton'));

interface Props {
  companyId: string;
  uniqueSymbol: string;
}

const getColumns = (total: number, max: number) => {
  if (total <= 0) {
    return 1;
  }
  if (total >= max) {
    return max;
  }

  return total;
};

export const MyNotes = ({ companyId, uniqueSymbol }: Props) => {
  const { openNoteModal, modalState } = useCompanyNoteModal();
  const { isMobile, isDesktop, isTablet } = useUserAgent();
  const theme = useTheme();
  const { data } = useCompanyNotes(companyId);
  const { Track, trackEvent } = useTracking({
    type: 'track',
    subject: 'MyNotes',
    companyId,
    uniqueSymbol,
  });

  const t = useOverviewTranslation();

  const hasNotes = typeof data !== 'undefined' && data.length > 0;
  const canCreateNote = !!companyId && !!uniqueSymbol;
  useEffect(() => {
    if (canCreateNote && modalState === 'adding') {
      openNoteModal({ companyId, uniqueSymbol });
    }
  }, [companyId, uniqueSymbol, modalState, canCreateNote, openNoteModal]);

  return (
    <Track>
      <Section data-cy-id="my-notes">
        <StyledHeaderGrid
          as="header"
          templateColumns="auto auto 1fr"
          gap="8px"
          justifyContent="start"
          alignItems="center"
        >
          <SubSectionHeader>{t('myNotes.heading')}</SubSectionHeader>
          {data && <Badge>{data.length}</Badge>}
          <div className="flex justify-end">
            {hasNotes && (
              <AddButton
                data-cy-id="my-notes-add"
                styleType="gentleReverse"
                disabled={!canCreateNote}
                onClick={() => {
                  trackEvent({
                    action: 'click',
                    modifier: 'add-note',
                    subject: 'my-notes-section-header',
                  });
                  openNoteModal({ companyId, uniqueSymbol });
                }}
              >
                <PlusSolid />
                &nbsp;{t('myNotes.addNote')}
              </AddButton>
            )}
          </div>
        </StyledHeaderGrid>
        <NotesContainer
          templateColumnsMd={`repeat(${getColumns(data?.length || 0, 3)}, 1fr)`}
          templateColumnsSm={`repeat(${getColumns(data?.length || 0, 2)}, 1fr)`}
          templateColumns="1fr"
          gap="8px"
        >
          <Media
            queries={{
              mobile: { maxWidth: theme.xsMaxRaw },
              tablet: { minWidth: theme.smMinRaw, maxWidth: theme.smMaxRaw },
              desktop: { minWidth: theme.lgMinRaw },
            }}
            defaultMatches={{
              mobile: isMobile,
              tablet: isTablet,
              desktop: isDesktop,
            }}
          >
            {(matches) => {
              const item = matches.mobile ? 1 : matches.tablet ? 2 : 3;
              return !hasNotes ? (
                <EmptyState
                  canCreateNote={canCreateNote}
                  handleAddNote={() => {
                    trackEvent({
                      action: 'click',
                      modifier: 'add-note',
                      subject: 'note-empty-state',
                    });
                    openNoteModal({ companyId, uniqueSymbol });
                  }}
                />
              ) : (
                data
                  .slice(0, item)
                  .map((note, i) => (
                    <Note
                      data-cy-id={`note-${i}`}
                      key={`note-${note.id}`}
                      note={note}
                      editNote={() =>
                        openNoteModal({ note, companyId, uniqueSymbol })
                      }
                    />
                  ))
              );
            }}
          </Media>
        </NotesContainer>
        {hasNotes && (
          <SeeMoreButton
            companyId={companyId}
            uniqueSymbol={uniqueSymbol}
            trackEvent={trackEvent}
          />
        )}
      </Section>
    </Track>
  );
};
